import { AccountType } from 'src/utils/enums/payment.enum';
import { formatDate } from 'src/utils/formatDate';
import { formatMoney } from 'src/utils/formatMoney';
import { forwardRef } from 'react';
import ImageLogo from 'src/assets/images/de-full-logo.png';
import { numberOrDefault } from 'src/utils/helpers';
import QuoteNotes from '../info/QuoteNotes';
import { useLocalization } from 'src/utils/useLocalization';

import './styles.scss';

interface IPrintProps {
    country: string;
    brand: string;
    accountType: string;
    account: any;
    quote: any;
}

interface IAccountInformationProps {
    accountType: string;
    account: any;
    quote: any;
}

interface IAccountListProps {
    items: Array<{ label: string; value: string }>;
}

const AccountListItem = ({
    label,
    value,
}: {
    label: string;
    value: string;
}) => {
    return (
        <div className='c-description-list__group no-flex-wrap'>
            <dt className='c-description-list__term item-label'>{label}</dt>
            <dd className='c-description-list__detail'>
                <span className='c-description-list__text bold item-value'>
                    {value}
                </span>
            </dd>
        </div>
    );
};

const AccountInfoList = ({ items }: IAccountListProps) => {
    return (
        <div className='o-layout__item  u-1/2'>
            <div className='c-description-list c-description-list--horizontal'>
                {items.map((item, index) => (
                    <AccountListItem
                        key={`${item.label + index}`}
                        label={item.label}
                        value={item.value}
                    />
                ))}
            </div>
        </div>
    );
};

const AccountInformation = ({
    accountType,
    account,
    quote,
}: IAccountInformationProps) => {
    const {
        payoffQuotesQuoteDetails,
        payments,
        payoffQuotesAccount,
        payoffQuoteInformation,
    } = useLocalization();
    const itemsCol1 = [
        {
            label: payments.PAYMENT_INFORMATION_ACCOUNTNUMBERLABEL,
            value: account.accountNumber,
        },
        {
            label: payments.PAYMENT_INFORMATION_CUSTOMERNAMELABEL,
            value: account.customerName,
        },
        {
            label: payoffQuotesQuoteDetails.ACCOUNT_INFO_TOTAL_PAYOUT,
            value: formatMoney(quote.quoteAmount),
        },
        {
            label: payoffQuotesAccount.LASTDATE,
            value: formatDate(account.lastPaymentDate),
        },
        {
            label: payments.PAYMENT_INFORMATION_TABLE_HEADERS_VIN,
            value: account.vin,
        },
    ];

    const itemsCol2 = [
        {
            label: payoffQuotesQuoteDetails.ACCOUNT_INFO_MATURITY,
            value: formatDate(account.originalMaturityDate),
        },
        {
            label: payoffQuoteInformation.QUOTE_DATE,
            value: formatDate(quote.quoteDate),
        },
    ];

    if (accountType == AccountType.OperatingLease) {
        const gstHst =
            numberOrDefault(quote.residualTax) +
            numberOrDefault(quote.futurePaymentTaxAmt) +
            numberOrDefault(quote.taxOnFees) +
            numberOrDefault(quote.pastDueTaxAmt);
        const amountBeforeTax = numberOrDefault(quote.quoteAmount) - gstHst;
        const leaseExtraFields = [
            {
                label: payoffQuotesAccount.AMTBEFORETAX,
                value: formatMoney(amountBeforeTax),
            },
            { label: payoffQuotesAccount.GST_HST, value: formatMoney(gstHst) },
        ];
        leaseExtraFields.forEach((field) => itemsCol2.push(field));
    }
    return (
        <section className='c-form-section quote-account-information u-mb-none'>
            <div className='c-form-section__content'>
                <div className='title'>
                    <h6>
                        <span className='c-section-heading__title-text u-mr-xxsmall section-title'>
                            {accountType == AccountType.OperatingLease
                                ? payoffQuotesQuoteDetails.ACCOUNT_INFO_LEASE
                                : payoffQuotesQuoteDetails.ACCOUNT_INFO_RETAIL}
                        </span>
                    </h6>
                </div>
                <div className='o-fieldset__row u-mt-large'>
                    <div className='o-layout'>
                        <AccountInfoList items={itemsCol1} />
                        <AccountInfoList items={itemsCol2} />
                    </div>
                </div>
            </div>
        </section>
    );
};

const OnlinePaymentAdvisory = () => {
    const { payoffQuotesQuoteDetails } = useLocalization();
    return (
        <div className='o-layout__item  u-1/1 onlinepayment'>
            <div className='title'>
                <h6>
                    <span className='c-section-heading__title-text u-mr-xxsmall section-title'>
                        {payoffQuotesQuoteDetails.ONLINE_PAYMENT_TITLE}
                    </span>
                </h6>
            </div>
            <div>{payoffQuotesQuoteDetails.ONLINE_PAYMENT_INFO}</div>
            <div>{payoffQuotesQuoteDetails.ONLINE_PAYMENT_INFO2}</div>
        </div>
    );
};

const Print = forwardRef<HTMLDivElement, IPrintProps>(function Print(
    { country, brand, accountType, account, quote },
    ref,
) {
    const { payoffQuotesDetailInfo } = useLocalization();
    return (
        <section className='c-form-section quote-print' ref={ref}>
            <div className='c-form-section__content'>
                <div className='u-mb'>
                    <a className='brand u-pb' href='#top'>
                        <img src={ImageLogo} height={16} width={155} alt='' />
                    </a>
                </div>
                <div className='o-fieldset__row'>
                    <AccountInformation
                        accountType={accountType}
                        account={account}
                        quote={quote}
                    />
                    <div className='o-layout'>
                        <div className='o-layout__item  u-1/1 quote-information-info-print'>
                            <div>{payoffQuotesDetailInfo.ADVISORY}</div>
                            <QuoteNotes
                                country={country}
                                brand={brand}
                                accountType={accountType}
                            />
                        </div>
                        <OnlinePaymentAdvisory />
                    </div>
                </div>
            </div>
        </section>
    );
});

export default Print;
